/* eslint-disable jsx-a11y/aria-props */
/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';

import Dropdown from '~components/inputs/dropdowns/Dropdown';
import Modal from '~components/modal/Modal';
import LocalizedString from '~components/text/LocalizedString';
import { localizedStringOrNodePropTypes } from '~components/text/LocalizedStringOrNode';
import { getLogger } from '~utils/logging';

import Button from '../buttons/Button';
import { automationIDPropTypes, getExtraAutomationID } from '../id/AutomationID';
import TextInput from '../inputs/TextInput';

const logger = getLogger(Symbol('Components:Page:Header:HeaderSearch:AdvancedSearchModal'));


/**
 * Converts the categories for the search to the format that is accepted by the <Dropdown> component
 *
 * @param {string|number} selectedCategory - the selected category in the dropdown set
 * @param {Array<object>} categories - the category list to be used for a <Dropdown> component
 * @returns {Array<object>} Returns an array with the selected category and the categories list reformatted
 */
const useValidValuesAsDropdownOptions = (selectedCategory, categories) => {
    const options = useMemo(() => {
        return categories.map((category) => ({
            value: category.key,
            label: category.label.key ? <LocalizedString localeKey={category.label.key} /> : category.label,
        }));
    }, [categories]);

    if (!selectedCategory) {
        return ['', options];
    } else {
        return [selectedCategory, options];
    }
};


/**
 * Categories dropdown for the Advanced Search modal.  This must be styled differently than the one on the header bar
 *
 * @param {object} props
 * @param {object} props.automationID - automation ID
 * @param {Array<object>} props.categories - the categories used to populate the dropdown
 * @param {string|number} props.selectedCategory - the current selected category
 * @param {PropTypes.func} props.setSelectedCategory - the function that updates the selected category
 * @returns {PropTypes.node} Returns a dropdown component withe the categories listed and the selected category selected
 */
const CategoriesMenu = ({ automationID, categories, selectedCategory, setSelectedCategory }) => {
    const [selectedOption, options] = useValidValuesAsDropdownOptions(selectedCategory, categories);

    if (!categories || categories.length === 0) {
        return null;
    }

    return <Dropdown
        automationID={automationID}
        label={<LocalizedString localeKey="ORDERING_UI.LABEL.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_CATALOGS" />}
        setValue={setSelectedCategory}
        value={selectedOption}
        options={options}
        required={false}
        type="button"
    />;
};

CategoriesMenu.propTypes = {
    automationID: automationIDPropTypes,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: localizedStringOrNodePropTypes,
        })
    ),
    selectedCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setSelectedCategory: PropTypes.func,
};

/**
 * Creates a modal for advanced catalog searching.  Allows a user to build a more complex search statement with OR/AND/""
 *
 * @param {object} props
 * @param {object} props.automationID - automation ID
 * @param {Function} props.toggleModal - a function that handles the modal toggle functionality
 * @param {Function} props.onSearch - a function that handles the action to be taken when the modal button is clicked
 * @param {Array<object>} props.categories - the categories used to populate the dropdown
 * @param {object} props.initialCategoryValue - the current selected category from the search bar
 * @returns {Modal} returns a modal containing a table of the review information
 */
const AdvancedSearchModal = ({ automationID, toggleModal, onSearch, categories, initialCategoryValue }) => {

    const [selectedCategory, setSelectedCategory] = useState(initialCategoryValue.key);
    const [searchAll, setSearchAll] = useState('');
    const [searchAny, setSearchAny] = useState('');
    const [searchExact, setSearchExact] = useState('');

    const onSubmit = useCallback(() => {
        let searchComplete = searchAll;
        if (searchAny) {
            searchComplete += ' ' + searchAny.trim().replace(/\s+/g, ' OR ');
        }
        if (searchExact) {
            searchComplete += ' "' + searchExact + '"';
        }

        searchComplete = searchComplete.trim();

        if (logger.isDebugEnabled()) {
            logger.debug(`HeaderSearch AdvancedSearchModal submitted`, { selectedCategory, searchComplete });
        }
        onSearch({ categoryKey: selectedCategory, search: searchComplete });
        toggleModal();
    }, [selectedCategory, searchAll, searchAny, searchExact, onSearch, toggleModal]);

    return (
        <Modal
            isOpen
            toggle={toggleModal}
            closeOnBGClick
            title={<LocalizedString localeKey="ORDERING_UI.SECTIONTITLE.HEADER__SEARCH__ADVANCED_SEARCH_MODAL" />}
            size="lg"
            className="advanced-search modal"
            footer={
                <Button automationID={automationID} action={onSubmit} type="primary" buttonType="submit">
                    <LocalizedString localeKey="ORDERING_UI.BUTTON.HEADER__SEARCH__ADVANCED_SEARCH_SUBMIT" />
                </Button>
            }
            footerClass="modal-footer--center"
        >
            <>
                <CategoriesMenu
                    automationID={getExtraAutomationID(automationID, 'advancedSearchCategory')}
                    categories={categories}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                />
                <TextInput
                    automationID={getExtraAutomationID(automationID, 'advancedSearchAll')}
                    label={<LocalizedString localeKey="ORDERING_UI.LABEL.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_ALL" />}
                    value={searchAll}
                    setValue={setSearchAll}
                    required={false}
                    invalid={false}
                    help={<LocalizedString localeKey="ORDERING_UI.MESSAGE.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_ALL" />}
                />
                <TextInput
                    automationID={getExtraAutomationID(automationID, 'advancedSearchAny')}
                    label={<LocalizedString localeKey="ORDERING_UI.LABEL.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_ANY" />}
                    value={searchAny}
                    setValue={setSearchAny}
                    required={false}
                    invalid={false}
                    help={<LocalizedString localeKey="ORDERING_UI.MESSAGE.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_ANY" />}
                />
                <TextInput
                    automationID={getExtraAutomationID(automationID, 'advancedSearchExact')}
                    label={<LocalizedString localeKey="ORDERING_UI.LABEL.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_EXACT" />}
                    value={searchExact}
                    setValue={setSearchExact}
                    required={false}
                    invalid={false}
                    help={<LocalizedString localeKey="ORDERING_UI.MESSAGE.HEADER__SEARCH__ADVANCED_SEARCH_MODAL__SEARCH_EXACT" />}
                />
            </>
        </Modal>
    );
};

AdvancedSearchModal.propTypes = {
    automationID: automationIDPropTypes,
    toggleModal: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: localizedStringOrNodePropTypes,
        })
    ),
    initialCategoryValue: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: localizedStringOrNodePropTypes,
    }),
};

export default AdvancedSearchModal;